<ng-template #coachDialog>
  <mat-dialog-content class="!surface-dark">
    <div class="tw-flex tw-flex-col tw-gap-2 tw-pb-2 tw-items-stretch">
      <div class="tw-w-full tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span class="uw-tag" [class.uw-tag-success]="state() === 'active'" [class.uw-tag-warning]="state() === 'joining'" [class.uw-tag-danger]="state() === 'error'" [class.uw-tag-secondary]="state() === 'idle'">{{ state() === 'active' ? 'Connected' : (state() === 'joining' ? 'Connecting' : (state() === 'error' ? 'Error' : 'Idle')) }}</span>
          <span class="tg-sm text-muted">Coaching session</span>
        </div>
        <div class="tg-sm text-muted tw-font-mono">{{ coachTimerText() }}</div>
      </div>

      @if(loadingState().message) {
      <div class="uw-message uw-message-info tg-sm">
        {{ loadingState().message }}
      </div>
      }

      @if(state() === 'error') {
      <div class="uw-card tw-flex tw-items-center tw-justify-between tw-gap-3">
        <div class="tg-sm text-muted">Coaching session is disconnected. Rejoin from the transfer row.</div>
      </div>
      }

      @if(transfer._metadata?.agent) {
      <div class="uw-card tw-space-y-1">
        <div class="tg-sm text-muted">Agent</div>
        <app-user-avatar [user]="transfer._metadata!.agent!" [showAvailability]="false" />
      </div>
      }

      @if(state() === 'active') {
      <div class="uw-card tw-space-y-3">
        <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <div class="tg-title">Coach Controls</div>
          <span class="uw-tag uw-tag-info">{{ coachMode() === 'barge' ? 'Barge' : 'Whisper' }}</span>
        </div>

        <mat-button-toggle-group aria-label="Coach mode" [ngModel]="coachMode()" (change)="setCoachMode($event.value)">
          <mat-button-toggle [value]="'whisper'" [disabled]="conferenceMutationPending() || coachMode() === 'whisper'">Whisper</mat-button-toggle>
          <mat-button-toggle [value]="'barge'" [disabled]="conferenceMutationPending() || coachMode() === 'barge'">Barge</mat-button-toggle>
        </mat-button-toggle-group>

        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <button mat-icon-button type="button" [disabled]="conferenceMutationPending()" (click)="toggleMute()"
            [attr.aria-label]="muted() ? 'Unmute coach' : 'Mute coach'" [matTooltip]="muted() ? 'Unmute' : 'Mute'">
            <mat-icon>{{ muted() ? 'mic_off' : 'mic' }}</mat-icon>
          </button>
          <div class="tg-sm text-muted">{{ stateDetail() }}</div>
          <span class="tw-flex-grow"></span>
          <button mat-flat-button class="tw-whitespace-nowrap btn-danger" type="button" (click)="leaveSession()"
            [disabled]="conferenceMutationPending()" aria-label="Leave call">
            <mat-icon>call_end</mat-icon>
            Leave call
          </button>
        </div>
      </div>

      <div class="uw-card uw-progress-bar-parent tw-space-y-3">
        <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <div class="tg-title">Main participant</div>
          <span class="uw-tag uw-tag-info">{{ mainParticipantStatusLabel() }}</span>
        </div>
        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span class="uw-tag uw-tag-secondary">Main</span>
          <span class="tg-sm text-muted tw-font-mono">{{ mainParticipantTimerText() }}</span>
        </div>
        @if(mainParticipantLeg()?.targetNumber) {
        <div class="tg-sm text-muted">{{ mainParticipantLeg()?.targetNumber }}</div>
        }
        <div class="tw-flex tw-items-center tw-justify-end tw-gap-1">
          <button mat-icon-button type="button"
            [disabled]="conferenceMutationPending() || !hasMainParticipantLeg()"
            (click)="onMainParticipantAction(mainParticipantLeg()?.muted ? 'unmute' : 'mute')"
            [attr.aria-label]="mainParticipantLeg()?.muted ? 'Unmute main participant' : 'Mute main participant'"
            [matTooltip]="mainParticipantLeg()?.muted ? 'Unmute main participant' : 'Mute main participant'">
            <mat-icon>{{ mainParticipantLeg()?.muted ? 'mic_off' : 'mic' }}</mat-icon>
          </button>
          <button mat-icon-button type="button"
            [disabled]="conferenceMutationPending() || !hasMainParticipantLeg()"
            (click)="onMainParticipantAction(mainParticipantLeg()?.onHold ? 'unhold' : 'hold')"
            [attr.aria-label]="mainParticipantLeg()?.onHold ? 'Resume main participant' : 'Hold main participant'"
            [matTooltip]="mainParticipantLeg()?.onHold ? 'Resume main participant' : 'Hold main participant'">
            <mat-icon>{{ mainParticipantLeg()?.onHold ? 'play_arrow' : 'pause' }}</mat-icon>
          </button>
          <button mat-icon-button type="button"
            [disabled]="conferenceMutationPending() || !hasMainParticipantLeg()"
            (click)="endMainParticipant()" aria-label="Remove main participant" matTooltip="Remove main participant" class="btn-danger">
            <mat-icon>call_end</mat-icon>
          </button>
        </div>
      </div>

      <div class="uw-card uw-progress-bar-parent tw-space-y-3">
        <div class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <div class="tg-title">Three-way participant</div>
          @if(hasThirdParticipantLeg()) {
          <span class="uw-tag" [class.uw-tag-info]="!thirdParticipantEnded()"
            [class.uw-tag-secondary]="thirdParticipantEnded()">{{ thirdParticipantStatusLabel() }}</span>
          }
        </div>
        @if(hasThirdParticipantLeg()) {
        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <span class="uw-tag uw-tag-secondary">Third</span>
          <span class="tg-sm text-muted tw-font-mono">{{ thirdParticipantTimerText() }}</span>
        </div>
        @if(thirdParticipantLeg()?.targetNumber) {
        <div class="tg-sm text-muted">{{ thirdParticipantLeg()?.targetNumber }}</div>
        }
        }
        @if(!hasThirdParticipantLeg() && !threeWayAccepted()) {
        <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <mat-form-field class="tw-flex-1 tw-min-w-0" appearance="outline" subscriptSizing="dynamic">
            <mat-label>Three-way number</mat-label>
            <input matInput [ngModel]="threeWayNumber()" (ngModelChange)="onThreeWayNumberChange($event)"
              [disabled]="state() !== 'active' || threeWayConnectionRecovering() || conferenceMutationPending()" />
          </mat-form-field>
          <button mat-icon-button type="button" [disabled]="!canStartThreeWay()" (click)="startThreeWayCall()"
            aria-label="Call third person" matTooltip="Call third person">
            <mat-icon>{{ placingThreeWay() ? 'hourglass_top' : 'person_add' }}</mat-icon>
          </button>
        </div>
        @if(threeWayConnectionRecovering()) {
        <div class="tg-sm text-muted">Call connection is recovering. Merge will be available when the call is stable.</div>
        }
        }

        @if(hasThirdParticipantLeg()) {
        <div class="tw-flex tw-items-center tw-justify-end tw-gap-1">
          <button mat-icon-button type="button"
            [disabled]="conferenceMutationPending() || thirdParticipantEnded()"
            (click)="onThirdParticipantAction(thirdParticipantLeg()?.muted ? 'unmute' : 'mute')"
            [attr.aria-label]="thirdParticipantLeg()?.muted ? 'Unmute third participant' : 'Mute third participant'"
            [matTooltip]="thirdParticipantLeg()?.muted ? 'Unmute third participant' : 'Mute third participant'">
            <mat-icon>{{ thirdParticipantLeg()?.muted ? 'mic_off' : 'mic' }}</mat-icon>
          </button>
          <button mat-icon-button type="button"
            [disabled]="conferenceMutationPending() || thirdParticipantEnded()"
            (click)="onThirdParticipantAction(thirdParticipantLeg()?.onHold ? 'unhold' : 'hold')"
            [attr.aria-label]="thirdParticipantLeg()?.onHold ? 'Resume third participant' : 'Hold third participant'"
            [matTooltip]="thirdParticipantLeg()?.onHold ? 'Resume third participant' : 'Hold third participant'">
            <mat-icon>{{ thirdParticipantLeg()?.onHold ? 'play_arrow' : 'pause' }}</mat-icon>
          </button>
          <button mat-icon-button type="button"
            [disabled]="conferenceMutationPending() || thirdParticipantEnded()"
            (click)="endThirdParticipant()" aria-label="Remove third participant" matTooltip="Remove third participant" class="btn-danger">
            <mat-icon>call_end</mat-icon>
          </button>
          @if(thirdParticipantEnded()) {
          <button mat-icon-button type="button" [disabled]="conferenceMutationPending()"
            (click)="dismissEndedThirdParticipant()" aria-label="Dismiss ended caller" matTooltip="Dismiss ended caller">
            <mat-icon>close</mat-icon>
          </button>
          }
        </div>
        }
      </div>

      <div class="tw-pt-2 tw-border-t border-neutral">
        <button mat-stroked-button color="warn" class="tw-whitespace-nowrap btn-danger tw-w-full" type="button" [disabled]="!hasConferenceParticipants() || conferenceMutationPending()"
          (click)="endConference()" aria-label="End conference for everyone" matTooltip="End conference for everyone">
          <mat-icon>{{ loadingState().endingConference ? 'hourglass_top' : 'call_end' }}</mat-icon>
          End conference
        </button>
      </div>
      }
    </div>
  </mat-dialog-content>

  <mat-dialog-actions class="uw-progress-bar-parent">
    @if(loadingState().showProgressBar) {
    <mat-progress-bar mode="indeterminate" class="uw-progress-bar uw-progress-bar-top"></mat-progress-bar>
    }
    <span class="tw-flex-grow"></span>
    <button class="tw-whitespace-nowrap" mat-stroked-button type="button" (click)="closeSession()" [disabled]="hasOngoingCall()">Close</button>
  </mat-dialog-actions>
</ng-template>

<ng-template #endConferenceConfirmation>
  <div mat-dialog-title>End conference for everyone?</div>
  <mat-dialog-content>This will disconnect you and every remaining participant.</mat-dialog-content>
  <mat-dialog-actions align="end">
    <button class="tw-whitespace-nowrap" mat-button [mat-dialog-close]="false" type="button">Keep conference</button>
    <button mat-flat-button class="tw-whitespace-nowrap btn-danger" [mat-dialog-close]="true" type="button">End conference</button>
  </mat-dialog-actions>
</ng-template>
